import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlParamReplicaId,
    urlProjectAdditionalDocs,
    urlProjectAdditionalDocFields,
    urlProjectAdditionalDocSchema,
    urlProjectAdditionalDocUnassign,
    urlProjectAdditionalDocLink,
    urlParamUniversalId,
} from '../../../Constants';
import { IAction, SlicePropsWithData } from '../../store';
import { IDocDefaultFields, IFieldConfig } from '../../project/documents/sliceProjectDocuments';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectAdditionalDocumentsState = {
    documents: {
        data: {},
        loading: false,
        error: null,
    },
    communicationDocuments: {},
    documentsToExpandNoteIds: [],
    fields: {
        data: [],
        loading: false,
        error: null,
    },
    schema: {
        data: [],
        loading: false,
        error: null,
    },
    additionalDocumentToRemove: {},
    isAdditionalDocumentRemoved: {
        data: false,
        loading: false,
        error: null,
    },
    isRemoveAdditionalDocDialogOpen: false,
    linkDocumentUrlPending: false,
    linkDocumentUrlHasError: false,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    /**
     * Clears the documents collection.
     * @param state - Current slice state object.
     */
    clearAdditionalDocuments(state: IProjectAdditionalDocumentsState) {
        state.documents.data = {};
    },

    /**
     * Sets the documentsToExpandNoteIds.
     * @param state - Current slice state object.
     * @param action - Action that contains the new documentsToExpandNoteIds.
     */
    setDocumentsToExpandNoteIds(
        state: IProjectAdditionalDocumentsState,
        action: IAction<string[]>,
    ) {
        state.documentsToExpandNoteIds = action.payload;
    },

    openRemoveAdditionalDocDialog(
        state: IProjectAdditionalDocumentsState,
        action?: IAction<IRemoveAdditionalDocument>,
    ) {
        state.isRemoveAdditionalDocDialogOpen = true;
        if (action.payload) {
            state.additionalDocumentToRemove = action.payload;
        }
    },
    closeRemoveAdditionalDocDialog(state: IProjectAdditionalDocumentsState) {
        state.isRemoveAdditionalDocDialogOpen = false;
    },
    resetRemoveAdditionalDocument(state: IProjectAdditionalDocumentsState) {
        state.isAdditionalDocumentRemoved.data = false;
    },
    resetRemoveAdditionalDocumentsError(state: IProjectAdditionalDocumentsState) {
        state.isAdditionalDocumentRemoved.error = null;
    },
    resetLinkDocumentUrlHasError(state: IProjectAdditionalDocumentsState) {
        state.linkDocumentUrlHasError = false;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches additional documents.
     * @param config - Object that contains the project database's replicaId and the idDocIndex to load documents for.
     * @returns Promise for the HTTP request.
     */
    getAdditionalDocuments: async (config: {
        replicaId: string;
        universalId: string;
        noteId: string;
        fields: string[];
    }): Promise<AdditionalDocsResponse> => {
        try {
            const { replicaId, universalId, fields } = config;

            const extraFields = ['RedliningPage'];

            const url = urlProjectAdditionalDocs.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                headers: {
                    universalId,
                    fieldset: [...fields, ...extraFields].join(';'),
                },
            };
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getAdditionalDocumentLink: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<string> => {
        try {
            const { replicaId, universalId } = config;
            const request: RequestInit = {
                headers: {
                    universalId,
                },
            };

            const url = urlProjectAdditionalDocLink.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that fetches the available field configurations for additional docs in a given project database.
     * @param replicaId - ReplicaId of the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectAdditionalDocFields: async (replicaId: string): Promise<IFieldConfig[]> => {
        try {
            const url = urlProjectAdditionalDocFields.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that fetches the available field schema for additional docs in a given project database.
     * @param replicaId - ReplicaId of the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectAdditionalDocFieldSchema: async (replicaId: string): Promise<string[]> => {
        try {
            const url = urlProjectAdditionalDocSchema.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    unassignAdditionalDocument: async (config: {
        replicaId: string;
        additionalDocument: string;
        mainDocument: string;
    }) => {
        try {
            const { replicaId, additionalDocument, mainDocument } = config;

            const url = urlProjectAdditionalDocUnassign
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, additionalDocument);

            const request: RequestInit = {
                headers: {
                    mainDocument,
                },
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectAdditionalDocumentsState>) => {
        /**
         * Reducers for the 'getAdditionalDocuments' thunk.
         */

        builder.addCase(
            getAdditionalDocs.pending,
            (state: Draft<IProjectAdditionalDocumentsState>) => {
                state.documents.loading = true;
                state.documents.error = null;
            },
        );
        builder.addCase(
            getAdditionalDocs.fulfilled,
            (state: Draft<IProjectAdditionalDocumentsState>, action) => {
                const { noteId } = action.meta.arg;
                state.documents.loading = false;
                state.documents.error = null;
                state.documents.data = {
                    ...state.documents.data,
                    ...{
                        [noteId]: action.payload.additional ?? [],
                    },
                };
                state.communicationDocuments = {
                    ...state.communicationDocuments,
                    ...{
                        [noteId]: action.payload.communication ?? [],
                    },
                };
            },
        );
        builder.addCase(
            getAdditionalDocs.rejected,
            (state: Draft<IProjectAdditionalDocumentsState>, action: IAction<SerializedError>) => {
                state.documents.loading = false;
                state.documents.error = action.error;
                state.documents.data = {};
            },
        );

        /**
         * Reducers for the 'getAdditionalDocFields' thunk.
         */

        builder.addCase(
            getAdditionalDocFields.pending,
            (state: Draft<IProjectAdditionalDocumentsState>) => {
                state.fields.loading = true;
                state.fields.error = null;
            },
        );
        builder.addCase(
            getAdditionalDocFields.fulfilled,
            (state: Draft<IProjectAdditionalDocumentsState>, action: IAction<IFieldConfig[]>) => {
                state.fields.loading = false;
                state.fields.error = null;
                state.fields.data = action.payload;
            },
        );
        builder.addCase(
            getAdditionalDocFields.rejected,
            (state: Draft<IProjectAdditionalDocumentsState>, action: IAction<SerializedError>) => {
                state.fields.loading = false;
                state.fields.error = action.error;
                state.fields.data = [];
            },
        );

        /**
         * Reducers for the 'getAdditionalDocFieldSchema' thunk.
         */

        builder.addCase(
            getAdditionalDocFieldSchema.pending,
            (state: Draft<IProjectAdditionalDocumentsState>) => {
                state.schema.loading = true;
                state.schema.error = null;
            },
        );
        builder.addCase(
            getAdditionalDocFieldSchema.fulfilled,
            (state: Draft<IProjectAdditionalDocumentsState>, action: IAction<string[]>) => {
                state.schema.loading = false;
                state.schema.error = null;
                state.schema.data = action.payload;
            },
        );
        builder.addCase(
            getAdditionalDocFieldSchema.rejected,
            (state: Draft<IProjectAdditionalDocumentsState>, action: IAction<SerializedError>) => {
                state.schema.loading = false;
                state.schema.error = action.error;
                state.schema.data = [];
            },
        );

        builder.addCase(
            unassignAdditionalDoc.pending,
            (state: Draft<IProjectAdditionalDocumentsState>) => {
                state.isAdditionalDocumentRemoved.loading = true;
                state.isAdditionalDocumentRemoved.error = null;
            },
        );
        builder.addCase(
            unassignAdditionalDoc.fulfilled,
            (state: Draft<IProjectAdditionalDocumentsState>) => {
                state.isAdditionalDocumentRemoved.data = true;
                state.isAdditionalDocumentRemoved.loading = false;
                state.isAdditionalDocumentRemoved.error = null;
            },
        );
        builder.addCase(
            unassignAdditionalDoc.rejected,
            (state: Draft<IProjectAdditionalDocumentsState>, action: IAction<SerializedError>) => {
                state.isAdditionalDocumentRemoved.loading = false;
                state.isAdditionalDocumentRemoved.error = action.error;
            },
        );

        builder.addCase(
            getAdditionalLinkDocument.pending,
            (state: Draft<IProjectAdditionalDocumentsState>) => {
                state.linkDocumentUrlPending = true;
            },
        );
        builder.addCase(
            getAdditionalLinkDocument.fulfilled,
            (state: Draft<IProjectAdditionalDocumentsState>, action: IAction<string>) => {
                state.linkDocumentUrlPending = false;
                window.open(action.payload, '_blank');
            },
        );
        builder.addCase(
            getAdditionalLinkDocument.rejected,
            (state: Draft<IProjectAdditionalDocumentsState>) => {
                state.linkDocumentUrlPending = false;
                state.linkDocumentUrlHasError = true;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getAdditionalDocs = createAsyncThunk(
    'project/additionalDocs',
    thunks.getAdditionalDocuments,
);
export const getAdditionalLinkDocument = createAsyncThunk(
    'project/additionalDocs/link',
    thunks.getAdditionalDocumentLink,
);
export const getAdditionalDocFields = createAsyncThunk(
    'project/additionalDocFields',
    thunks.getProjectAdditionalDocFields,
);
export const getAdditionalDocFieldSchema = createAsyncThunk(
    'project/additionalDocFieldSchema',
    thunks.getProjectAdditionalDocFieldSchema,
);
export const unassignAdditionalDoc = createAsyncThunk(
    'project/additionalDocUnassign',
    thunks.unassignAdditionalDocument,
);

type AdditionalDocsResponse = {
    additional: IDocDefaultFields[];
    communication: IDocDefaultFields[];
};

/** Dictionary that contains the additional document children of project documents, with the document's NoteId being the object key.  */
export type DocumentCollection = { [id: string]: IDocDefaultFields[] };

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectAdditionalDocumentsState {
    /** Details on the requested additional documents in the current project database. */
    documents: SlicePropsWithData<DocumentCollection>;

    /** Communication documents to the document. */
    communicationDocuments: DocumentCollection;

    /** The NoteIds of the documents that should be expanded. */
    documentsToExpandNoteIds: string[];

    /** Data on the field configurations for the project view. */
    fields: SlicePropsWithData<IFieldConfig[]>;

    /** View Field Schema. */
    schema: SlicePropsWithData<string[]>;

    /** Additional document that gets deleted. */
    additionalDocumentToRemove: IRemoveAdditionalDocument;

    /** Shows if the additional document is removed. */
    isAdditionalDocumentRemoved: SlicePropsWithData<boolean>;

    /** Boolean value if the remove additional document dialog is open. */
    isRemoveAdditionalDocDialogOpen: boolean;

    linkDocumentUrlPending: boolean;
    linkDocumentUrlHasError: boolean;
}

interface IRemoveAdditionalDocument {
    /** ReplicaId of the selected project. */
    replicaId?: string;

    /** Document id of the additional document. */
    universalId?: string;

    /** Document id of the referenced main document. */
    referencedMainDocId?: string;

    /** Subject name of the additional document. */
    subject?: string;
}

/**
 * Create the redux slice.
 */
export const projectAdditionalDocumentsSlice = createSlice({
    name: 'projectAdditionalDocuments',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    clearAdditionalDocuments,
    setDocumentsToExpandNoteIds,
    openRemoveAdditionalDocDialog,
    closeRemoveAdditionalDocDialog,
    resetRemoveAdditionalDocument,
    resetRemoveAdditionalDocumentsError,
    resetLinkDocumentUrlHasError,
} = projectAdditionalDocumentsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectAdditionalDocumentsSlice.reducer;
